<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
  transferId: { type: [String, Number], default: null },
  targetInboxId: { type: [String, Number], default: null },
});

const emit = defineEmits(['dismiss']);

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('inboxConversationTransfers/getUIFlags');
const currentTransfer = useMapGetter(
  'inboxConversationTransfers/getCurrentTransfer'
);
const accountId = useMapGetter('getCurrentAccountId');

const POLL_INTERVAL_MS = 5000;
const POLLING_STATUSES = new Set(['pending', 'processing']);

const pollHandle = ref(null);

const refresh = async () => {
  if (!props.transferId) return;
  try {
    await store.dispatch('inboxConversationTransfers/fetchTransfer', {
      inboxId: props.inboxId,
      transferId: props.transferId,
    });
  } catch (_) {
    // surface no alert here — keep the tracker resilient against transient errors
  }
};

const stopPolling = () => {
  if (pollHandle.value) {
    clearInterval(pollHandle.value);
    pollHandle.value = null;
  }
};

const startPolling = () => {
  stopPolling();
  pollHandle.value = setInterval(refresh, POLL_INTERVAL_MS);
};

const status = computed(() => currentTransfer.value?.status || 'pending');

const statusLabel = computed(() => {
  switch (status.value) {
    case 'completed':
      return t('INBOX_MGMT.CONVERSATION_TRANSFER.STATUS.COMPLETED');
    case 'processing':
      return t('INBOX_MGMT.CONVERSATION_TRANSFER.STATUS.PROCESSING');
    case 'failed':
      return t('INBOX_MGMT.CONVERSATION_TRANSFER.STATUS.FAILED');
    case 'cancelled':
      return t('INBOX_MGMT.CONVERSATION_TRANSFER.STATUS.CANCELLED');
    default:
      return t('INBOX_MGMT.CONVERSATION_TRANSFER.STATUS.PENDING');
  }
});

const statusBadgeClasses = computed(() => {
  switch (status.value) {
    case 'completed':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
    case 'processing':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
    case 'failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
    case 'cancelled':
      return 'bg-n-alpha-3 text-n-slate-11';
    default:
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
  }
});

const formatTimestamp = unix => {
  if (!unix) return '';
  const ms = Number(unix) * 1000;
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleString();
};

const startedAt = computed(() =>
  formatTimestamp(currentTransfer.value?.started_at)
);
const completedAt = computed(() =>
  formatTimestamp(currentTransfer.value?.completed_at)
);
const currentStep = computed(() => currentTransfer.value?.current_step || '');
const errorMessage = computed(() => currentTransfer.value?.error_message || '');
const retryCount = computed(() => currentTransfer.value?.retry_count || 0);

const targetInboxLink = computed(() => {
  if (!props.targetInboxId || !accountId.value) return '';
  return frontendURL(
    `accounts/${accountId.value}/custom_view?inbox=${props.targetInboxId}`
  );
});

const targetInboxConversationsLink = computed(() => {
  if (!props.targetInboxId || !accountId.value) return '';
  return frontendURL(
    `accounts/${accountId.value}/inbox/${props.targetInboxId}`
  );
});

const canCancel = computed(() => status.value === 'pending');
const canRetry = computed(() => status.value === 'failed');
const isFinal = computed(() =>
  ['completed', 'failed', 'cancelled'].includes(status.value)
);

const onCancel = async () => {
  try {
    await store.dispatch('inboxConversationTransfers/cancelTransfer', {
      inboxId: props.inboxId,
      transferId: props.transferId,
    });
    useAlert(t('INBOX_MGMT.CONVERSATION_TRANSFER.API.CANCEL_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.CONVERSATION_TRANSFER.API.CANCEL_ERROR')
    );
  }
};

const onRetry = async () => {
  try {
    await store.dispatch('inboxConversationTransfers/retryTransfer', {
      inboxId: props.inboxId,
      transferId: props.transferId,
    });
    useAlert(t('INBOX_MGMT.CONVERSATION_TRANSFER.API.RETRY_SUCCESS'));
    startPolling();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.CONVERSATION_TRANSFER.API.RETRY_ERROR')
    );
  }
};

watch(status, value => {
  if (POLLING_STATUSES.has(value)) {
    startPolling();
  } else {
    stopPolling();
  }
});

watch(
  () => props.transferId,
  newId => {
    if (!newId) {
      stopPolling();
      return;
    }
    refresh();
    startPolling();
  }
);

onMounted(() => {
  if (props.transferId) {
    refresh();
    startPolling();
  }
});

onBeforeUnmount(() => {
  stopPolling();
});
</script>

<template>
  <div
    class="flex flex-col gap-4 p-4 rounded-md border border-n-weak bg-n-alpha-black1"
  >
    <div class="flex justify-between items-center gap-4">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.TITLE') }}
      </h4>
      <span
        class="px-2 py-0.5 text-xs font-medium rounded-full"
        :class="statusBadgeClasses"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div class="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
      <div>
        <p class="text-xs uppercase text-n-slate-11">
          {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.CURRENT_STEP') }}
        </p>
        <p class="text-n-slate-12">{{ currentStep || '—' }}</p>
      </div>
      <div>
        <p class="text-xs uppercase text-n-slate-11">
          {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.STARTED_AT') }}
        </p>
        <p class="text-n-slate-12">{{ startedAt || '—' }}</p>
      </div>
      <div>
        <p class="text-xs uppercase text-n-slate-11">
          {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.COMPLETED_AT') }}
        </p>
        <p class="text-n-slate-12">{{ completedAt || '—' }}</p>
      </div>
      <div>
        <p class="text-xs uppercase text-n-slate-11">
          {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.RETRY_COUNT') }}
        </p>
        <p class="text-n-slate-12">{{ retryCount }}</p>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="p-3 rounded-md border border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-900/20"
    >
      <p
        class="mb-1 text-xs font-semibold uppercase text-red-700 dark:text-red-300"
      >
        {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.ERROR_LABEL') }}
      </p>
      <p class="text-sm text-red-700 dark:text-red-200">{{ errorMessage }}</p>
    </div>

    <div v-if="status === 'completed' && targetInboxLink" class="text-sm">
      <a
        :href="targetInboxConversationsLink"
        class="text-n-brand hover:underline"
      >
        {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.VIEW_TARGET_INBOX') }}
      </a>
    </div>

    <div class="flex gap-2 justify-end flex-wrap">
      <NextButton
        ghost
        slate
        :label="$t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.BUTTONS.REFRESH')"
        :is-loading="uiFlags.isFetchingItem"
        @click="refresh"
      />
      <NextButton
        v-if="canCancel"
        ghost
        ruby
        :label="$t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.BUTTONS.CANCEL')"
        :is-loading="uiFlags.isCancelling"
        @click="onCancel"
      />
      <NextButton
        v-if="canRetry"
        amber
        :label="$t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.BUTTONS.RETRY')"
        :is-loading="uiFlags.isRetrying"
        @click="onRetry"
      />
      <NextButton
        v-if="isFinal"
        ghost
        slate
        :label="$t('INBOX_MGMT.CONVERSATION_TRANSFER.PROGRESS.BUTTONS.DISMISS')"
        @click="emit('dismiss')"
      />
    </div>
  </div>
</template>
